
import Vue from 'vue';

import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import GridSkeletonLoader from '@root/common/components/base/loader/GridSkeletonLoader.vue';
import isMobileView from '@root/common/utils/isMobileView';
import { K5AStreamRenderReadyData, KilkayaWindow } from '@root/modules/kilkaya/types/kilkaya';

interface Data {
  hasLoadedOnce: boolean;
  hasFailed: boolean;
  isMobileView: boolean;
  loadTimeout: number | undefined;
  pendingSetupPromise: Promise<void> | undefined;
}

interface Methods {
  handleStreamError: (error: unknown, status: 'error_initialization' | 'error_timeout') => void;
  handleStreamReady: (event: CustomEvent<K5AStreamRenderReadyData>) => void;
  setupStream: () => Promise<void>;
}

interface Props {
  slotName: string;
}

declare const window: KilkayaWindow;

// Make Vue ignore k5a-stream markup
Vue.config.ignoredElements = [/^k5a-/];

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    AdsLayout,
    GridSkeletonLoader,
  },
  props: {
    slotName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hasLoadedOnce: false,
      hasFailed: false,
      isMobileView: false,
      loadTimeout: undefined,
      pendingSetupPromise: undefined,
    };
  },
  methods: {
    handleStreamReady(event: CustomEvent<K5AStreamRenderReadyData>) {
      console.log('K5A:StreamRenderReady');

      const detail = event.detail;

      if (detail.slotName && detail.slotName !== this.slotName) {
        return;
      }

      if (this.loadTimeout) {
        window.clearTimeout(this.loadTimeout);
        this.loadTimeout = undefined;
      }

      this.hasLoadedOnce = true;
    },
    handleStreamError(error: unknown, status: 'error_initialization' | 'error_timeout') {
      if (this.hasFailed) {
        return;
      }

      this.hasFailed = true;

      if (this.loadTimeout) {
        window.clearTimeout(this.loadTimeout);
        this.loadTimeout = undefined;
      }

      this.$sentry.captureException(error, {
        tags: { componentName: 'KilkayaFeed', status },
        contexts: { data: { slotName: this.slotName } },
      });

      this.$emit('fallback');
    },
    setupStream(): Promise<void> {
      console.log('K5A:StreamReady');

      if (this.pendingSetupPromise) {
        return this.pendingSetupPromise;
      }

      this.pendingSetupPromise = window.k5aStreamReadyPromise
        .then(() => {
          try {
            window.kilkaya?.pageData?.reRead(); // syncs window.k5aMeta into pageData
            window.kilkaya?.pilash?.hardInit(); // clears render cache and renders existing <k5a-stream> elements on the page
          } catch (error) {
            this.handleStreamError(error, 'error_initialization');
          }
        })
        .finally(() => {
          this.pendingSetupPromise = undefined;
        });

      return this.pendingSetupPromise;
    },
  },
  beforeMount() {
    document.addEventListener('K5A:StreamRenderReady', this.handleStreamReady);
  },
  async mounted() {
    this.isMobileView = isMobileView();

    await this.$nextTick();

    this.loadTimeout = window.setTimeout(() => {
      this.handleStreamError(new Error('Kilkaya stream load timed out'), 'error_timeout');
    }, 3000);

    try {
      await this.setupStream();
    } catch (error) {
      this.handleStreamError(error, 'error_initialization');
    }
  },
  beforeDestroy() {
    if (this.loadTimeout) {
      window.clearTimeout(this.loadTimeout);
    }

    document.removeEventListener('K5A:StreamRenderReady', this.handleStreamReady);
  },
});
